import { supabase } from '@/integrations/supabase/client';

// Cache to avoid repeated DB lookups
export interface AssessmentMeta {
  id: string;
  title: string;
  description: string | null;
  estimatedMinutes: number | null;
}

let assessmentCache: Record<string, AssessmentMeta> | null = null;

/**
 * Fetches the assessment UUID for a given module_id.
 * Uses the assessments table's module_id column.
 * Results are cached after first fetch.
 */
export async function getAssessmentIdByModuleId(moduleId: string): Promise<string | null> {
  if (!assessmentCache) {
    const { data, error } = await supabase
      .from('assessments')
      .select('id, module_id, assessment_name, description, estimated_duration_minutes');

    if (error || !data) {
      console.error('Failed to fetch assessments:', error);
      return null;
    }

    assessmentCache = {};
    for (const row of data) {
      const moduleKey = (row as any).module_id;
      if (moduleKey) {
        assessmentCache[moduleKey] = {
          id: row.id,
          title: row.assessment_name,
          description: row.description,
          estimatedMinutes: row.estimated_duration_minutes,
        };
      }
    }
  }

  return assessmentCache[moduleId]?.id || null;
}

/**
 * Returns the full metadata for a given module_id (cached).
 */
export async function getAssessmentMetaByModuleId(moduleId: string): Promise<AssessmentMeta | null> {
  if (!assessmentCache) {
    await getAssessmentIdByModuleId(moduleId); // triggers cache load
  }
  return assessmentCache?.[moduleId] || null;
}

/**
 * Returns all assessment records (cached).
 */
export async function getAllAssessments(): Promise<Record<string, AssessmentMeta>> {
  if (!assessmentCache) {
    await getAssessmentIdByModuleId(''); // triggers cache load
  }
  return assessmentCache || {};
}

/** Clear cache (useful after data changes) */
export function clearAssessmentCache(): void {
  assessmentCache = null;
}
